/**
 * Titularidad del Portal Condominio con datos reales (migración 20261015240000).
 *
 * El dueño en condominio son los compradores de la cuenta de mantenimiento: lo que ve la app de
 * clientes. Aprobar una solicitud o cambiar el titular a mano lo hace la RPC
 * `condominio_reasignar_titularidad`: da de baja a los anteriores, da de alta a los nuevos (el
 * trigger de compradores les da acceso a la app: crea la cuenta o la asigna si ya existe) y deja
 * el cambio en `condominio_cambios_titularidad`. La cuenta de la venta no se toca.
 */
import { supabase } from "@/integrations/supabase/client";
import { safeExtension } from "@/lib/storage-keys";
import { esRpcInexistente } from "@/lib/rpcErrors";
import { RpcCondominioPendiente } from "./api";
import type { StatusTone } from "@/components/ui/status-pill";
import { centavos } from "./unidades";
import { RUTA_PORTAL_CONDOMINIO, rutaUnidadCondominio } from "./rutas";

const sb = supabase as any;
const IDS_VACIO = [-1];

// ─── Solicitudes de propietario (onboarding público) ─────────────────────────

export type EstatusSolicitud = "pendiente" | "en_revision" | "aprobada_n1" | "aprobada_n2" | "rechazada";

export const ETIQUETA_ESTATUS_SOLICITUD: Record<EstatusSolicitud, string> = {
  pendiente: "Nueva",
  en_revision: "En revisión",
  aprobada_n1: "Aprobada",
  aprobada_n2: "Aprobada (registral)",
  rechazada: "Rechazada",
};

export const RUTA_TITULARIDAD = `${RUTA_PORTAL_CONDOMINIO}/titularidad`;
export const RUTA_TITULARIDAD_LEGAL_FLOW = "/admin/legal-flow/titularidad";

/**
 * Portal desde el que se atiende Titularidad (#1364). Condominio y Legal Flow leen y escriben
 * las mismas solicitudes con las mismas RPC; cambia la ruta, el permiso que se pide y a dónde
 * lleva la cuenta de la unidad.
 */
export interface PortalTitularidad {
  /** Bandeja; el detalle es `${ruta}/:id`. También es el submenú cuyo permiso `aprobar` se pide. */
  ruta: string;
  /** Detalle de la cuenta de mantenimiento, o null si el portal no tiene esa pantalla. */
  rutaCuenta: ((idCuenta: number) => string) | null;
}

export const PORTAL_TITULARIDAD_CONDOMINIO: PortalTitularidad = {
  ruta: RUTA_TITULARIDAD,
  rutaCuenta: rutaUnidadCondominio,
};

/** Legal no siempre tiene el Portal Condominio: la cuenta de la unidad se muestra sin liga. */
export const PORTAL_TITULARIDAD_LEGAL_FLOW: PortalTitularidad = {
  ruta: RUTA_TITULARIDAD_LEGAL_FLOW,
  rutaCuenta: null,
};

export const TONO_SOLICITUD: Record<EstatusSolicitud, StatusTone> = {
  pendiente: "info",
  en_revision: "warning",
  aprobada_n1: "success",
  aprobada_n2: "success",
  rechazada: "danger",
};

export const solicitudAbierta = (e: EstatusSolicitud) => e === "pendiente" || e === "en_revision";

export interface DocumentoSolicitud {
  id: number;
  tipo: string;
  url: string | null;
  estatus: "en_revision" | "validado" | "rechazado" | "expirado";
  fecha: string | null;
}

export interface VerificacionSolicitud {
  clave: string;
  etiqueta: string;
  resultado: "ok" | "revisar" | "falla";
}

export interface SolicitudTitularidad {
  id: number;
  caso: string;
  id_propiedad: number;
  unidad: string;
  id_persona: number | null;
  nombre: string;
  email: string;
  telefono: string | null;
  rfc: string | null;
  tipo_persona: "pf" | "pm";
  tipo_compra: string | null;
  antiguedad_compra: string | null;
  nivel: number;
  estatus: EstatusSolicitud;
  comentario: string | null;
  fecha: string;
  /** Fecha y hora completas de recepción (ISO): el tiempo sin atender se mide en días y horas. */
  recibida_en: string | null;
  fecha_revision: string | null;
  documentos: DocumentoSolicitud[];
  verificacion: VerificacionSolicitud[];
}

const ESTATUS_DOC: Record<number, DocumentoSolicitud["estatus"]> = { 1: "en_revision", 2: "validado", 3: "rechazado", 4: "expirado" };
const ESTATUS: EstatusSolicitud[] = ["pendiente", "en_revision", "aprobada_n1", "aprobada_n2", "rechazada"];

/**
 * Solicitudes activas de las unidades dadas (las del condominio elegido). Lectura directa: son
 * decenas de filas y RLS acota por permiso `leer` (Titularidad, Alta Dirección o Legal Flow) y por
 * proyecto (20261022095841). Escribir solo se puede por las RPC de resolver y reasignar.
 */
export async function fetchSolicitudesTitularidad(idsPropiedad: number[]): Promise<SolicitudTitularidad[]> {
  if (idsPropiedad.length === 0) return [];
  const { data: sols, error } = await sb
    .from("solicitudes_propietario")
    .select("id, case_id, id_propiedad, id_persona, email, tipo_persona, tipo_compra, antiguedad_compra, nivel, estatus, verificacion, comentario_revision, fecha_creacion, fecha_revision")
    .eq("activo", true)
    .in("id_propiedad", idsPropiedad)
    .order("fecha_creacion", { ascending: false });
  if (error) throw error;
  const filas = (sols ?? []) as any[];
  if (filas.length === 0) return [];

  const personaIds = [...new Set(filas.map((r) => r.id_persona).filter(Boolean))] as number[];
  const propIds = [...new Set(filas.map((r) => r.id_propiedad))] as number[];
  const [pe, pr, dc] = await Promise.all([
    sb.from("personas").select("id, nombre_legal, nombre_comercial, rfc, telefono").in("id", personaIds.length ? personaIds : IDS_VACIO),
    sb.from("propiedades").select("id, numero_propiedad").in("id", propIds.length ? propIds : IDS_VACIO),
    sb.from("documentos").select("id, id_persona, id_tipo_documento, url, id_estatus_verificacion, fecha_creacion")
      .in("id_persona", personaIds.length ? personaIds : IDS_VACIO).eq("activo", true).eq("es_draft", false),
  ]);
  for (const r of [pe, pr, dc]) if (r.error) throw r.error;
  const tipoIds = [...new Set(((dc.data ?? []) as any[]).map((d) => d.id_tipo_documento).filter(Boolean))];
  const td = tipoIds.length ? await sb.from("tipos_documento").select("id, nombre").in("id", tipoIds) : { data: [], error: null };
  if (td.error) throw td.error;

  const persona = new Map<number, any>(((pe.data ?? []) as any[]).map((p) => [p.id, p]));
  const unidad = new Map<number, string>(((pr.data ?? []) as any[]).map((p) => [p.id, String(p.numero_propiedad ?? "")]));
  const tipoDoc = new Map<number, string>(((td.data ?? []) as any[]).map((t) => [t.id, t.nombre]));
  const docs = new Map<number, DocumentoSolicitud[]>();
  for (const d of (dc.data ?? []) as any[]) {
    const lista = docs.get(d.id_persona) ?? [];
    lista.push({
      id: d.id,
      tipo: tipoDoc.get(d.id_tipo_documento) ?? "Documento",
      url: d.url ?? null,
      estatus: ESTATUS_DOC[d.id_estatus_verificacion] ?? "en_revision",
      fecha: d.fecha_creacion ? String(d.fecha_creacion).slice(0, 10) : null,
    });
    docs.set(d.id_persona, lista);
  }

  return filas.map((r) => {
    const p = persona.get(r.id_persona) ?? {};
    const est = String(r.estatus) as EstatusSolicitud;
    const verif = Array.isArray(r.verificacion) ? (r.verificacion as any[]) : [];
    return {
      id: Number(r.id),
      caso: String(r.case_id ?? r.id),
      id_propiedad: Number(r.id_propiedad),
      unidad: unidad.get(r.id_propiedad) ?? "",
      id_persona: r.id_persona ?? null,
      nombre: p.nombre_legal || p.nombre_comercial || r.email || "Sin nombre",
      email: String(r.email ?? ""),
      telefono: p.telefono ?? null,
      rfc: p.rfc ?? null,
      tipo_persona: r.tipo_persona === "moral" || r.tipo_persona === "pm" ? "pm" : "pf",
      tipo_compra: r.tipo_compra ?? null,
      antiguedad_compra: r.antiguedad_compra ?? null,
      nivel: Number(r.nivel) || 0,
      estatus: ESTATUS.includes(est) ? est : "pendiente",
      comentario: r.comentario_revision ?? null,
      fecha: String(r.fecha_creacion ?? "").slice(0, 10),
      recibida_en: r.fecha_creacion ? String(r.fecha_creacion) : null,
      fecha_revision: r.fecha_revision ? String(r.fecha_revision).slice(0, 10) : null,
      documentos: (docs.get(r.id_persona) ?? []).sort((a, b) => (b.fecha ?? "").localeCompare(a.fecha ?? "")),
      verificacion: verif.map((v) => ({
        clave: String(v.key ?? v.label ?? ""),
        etiqueta: String(v.label ?? v.key ?? "Verificación"),
        resultado: v.status === "ok" ? "ok" : v.status === "fail" ? "falla" : "revisar",
      })),
    } satisfies SolicitudTitularidad;
  });
}

// ─── Historial de cambios ────────────────────────────────────────────────────

export interface TitularCambio { id_persona: number; nombre: string; porcentaje: number }
export interface DocumentoCambio { path: string; url: string; nombre: string }

export interface CambioTitularidad {
  id: number;
  id_cuenta: number;
  id_propiedad: number;
  id_solicitud: number | null;
  anteriores: TitularCambio[];
  nuevos: TitularCambio[];
  justificacion: string;
  documentos: DocumentoCambio[];
  aprobado_por: string | null;
  fecha: string;
}

export async function fetchCambiosTitularidad(proyectoId: number): Promise<CambioTitularidad[]> {
  const { data, error } = await sb
    .from("condominio_cambios_titularidad")
    .select("id, id_cuenta_cobranza, id_propiedad, id_solicitud, titulares_anteriores, titulares_nuevos, justificacion, documentos, aprobado_por_email, fecha")
    .eq("id_proyecto", proyectoId)
    .order("fecha", { ascending: false })
    .limit(500);
  if (error) {
    // Tabla aún sin migrar en este ambiente: aviso claro en vez de "No pudimos cargar".
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  const titulares = (v: unknown): TitularCambio[] =>
    (Array.isArray(v) ? v : []).map((t: any) => ({ id_persona: Number(t.id_persona), nombre: String(t.nombre ?? ""), porcentaje: Number(t.porcentaje) || 0 }));
  return ((data ?? []) as any[]).map((r) => ({
    id: Number(r.id),
    id_cuenta: Number(r.id_cuenta_cobranza),
    id_propiedad: Number(r.id_propiedad),
    id_solicitud: r.id_solicitud != null ? Number(r.id_solicitud) : null,
    anteriores: titulares(r.titulares_anteriores),
    nuevos: titulares(r.titulares_nuevos),
    justificacion: String(r.justificacion ?? ""),
    documentos: (Array.isArray(r.documentos) ? r.documentos : []) as DocumentoCambio[],
    aprobado_por: r.aprobado_por_email ?? null,
    fecha: String(r.fecha ?? ""),
  }));
}

// ─── Historial de una solicitud (#1364) ──────────────────────────────────────

/** Un cambio de estatus: quién, cuándo, de qué a qué y con qué comentario. */
export interface EventoSolicitud {
  id: number;
  fecha: string;
  /** null = alta de la solicitud. */
  anterior: EstatusSolicitud | null;
  nuevo: EstatusSolicitud;
  comentario: string | null;
  quien: string;
}

/** Un correo de los avisos de titularidad, uno por destinatario, con su resultado. */
export interface CorreoSolicitud {
  id: number;
  fecha: string;
  clave: string;
  destinatario: string;
  estado: string;
  error: string | null;
}

export interface HistorialSolicitud {
  estatus: EventoSolicitud[];
  correos: CorreoSolicitud[];
}

/** Qué aviso fue y a quién va (claves de notificaciones_configuracion, migración 20261022034453). */
export const ETIQUETA_AVISO_TITULARIDAD: Record<string, string> = {
  titularidad_solicitud_recibida: "Solicitud recibida · titular registrado",
  titularidad_en_revision: "En revisión · solicitante",
  titularidad_rechazada: "Rechazo con motivo · solicitante",
  titularidad_aprobada: "Aprobación y acceso · nuevo titular",
  titularidad_aviso_interno: "Aviso al equipo",
};

/** Estado del envío en avisos_correo_envios. «Enviado» = Postmark lo aceptó, no que llegó al buzón. */
export const ESTADO_CORREO: Record<string, { etiqueta: string; tono: StatusTone }> = {
  pendiente: { etiqueta: "Pendiente", tono: "neutral" },
  aceptado: { etiqueta: "Enviado", tono: "info" },
  delivered: { etiqueta: "Entregado", tono: "success" },
  bounced: { etiqueta: "Rebotado", tono: "warning" },
  error: { etiqueta: "Error", tono: "danger" },
};

const estatusValido = (v: unknown): EstatusSolicitud | null =>
  ESTATUS.includes(v as EstatusSolicitud) ? (v as EstatusSolicitud) : null;

/** Respuesta de la RPC `titularidad_historial` a tipos del front (tolera campos faltantes). */
export function normalizarHistorial(raw: unknown): HistorialSolicitud {
  const r = (raw ?? {}) as { estatus?: unknown; correos?: unknown };
  const lista = (v: unknown) => (Array.isArray(v) ? v : []) as any[];
  return {
    estatus: lista(r.estatus).map((e) => {
      const anterior = estatusValido(e.estatus_anterior);
      return {
        id: Number(e.id),
        fecha: String(e.fecha ?? ""),
        anterior,
        nuevo: estatusValido(e.estatus_nuevo) ?? "pendiente",
        comentario: e.comentario ?? null,
        quien: e.usuario_nombre || e.usuario_email || (anterior == null ? "Registro público" : "Sistema"),
      };
    }),
    correos: lista(r.correos).map((c) => ({
      id: Number(c.id),
      fecha: String(c.fecha ?? ""),
      clave: String(c.clave ?? ""),
      destinatario: String(c.destinatario ?? ""),
      estado: String(c.estado ?? "pendiente"),
      error: c.error ?? null,
    })),
  };
}

/**
 * Desarrollo de una solicitud (propiedad → modelo → edificio → proyecto). Legal Flow abre el
 * detalle desde el correo interno sin saber de qué desarrollo es. Null si no existe o la RLS no
 * deja verla.
 */
export async function fetchProyectoDeSolicitud(idSolicitud: number): Promise<number | null> {
  const { data, error } = await sb
    .from("solicitudes_propietario")
    .select("id, propiedades!inner(edificios_modelos!inner(edificios!inner(id_proyecto)))")
    .eq("id", idSolicitud)
    .eq("activo", true)
    .maybeSingle();
  if (error) throw error;
  const id = data?.propiedades?.edificios_modelos?.edificios?.id_proyecto;
  return id != null ? Number(id) : null;
}

/** Historial y correos de una solicitud. Null si la RPC aún no existe en este ambiente. */
export async function fetchHistorialSolicitud(idSolicitud: number): Promise<HistorialSolicitud | null> {
  const { data, error } = await sb.rpc("titularidad_historial", { p_id_solicitud: idSolicitud });
  if (error) {
    if (esRpcInexistente(error)) return null;
    throw error;
  }
  return normalizarHistorial(data);
}

// ─── Reasignar ───────────────────────────────────────────────────────────────

export interface PersonaEncontrada {
  id_persona: number;
  nombre: string;
  email: string | null;
  rfc: string | null;
  tiene_app: boolean;
}

/**
 * Busca personas para hacerlas titulares, siempre con la RPC `condominio_buscar_personas`, que
 * valida el permiso (Super Admin o `aprobar` en Titularidad):
 * - 3 letras o más: nombre, correo o RFC.
 * - Menos de 3 (o vacío): las primeras personas activas en orden alfabético, filtrando por nombre
 *   si hay 1-2 letras (20261022095843). Antes este caso leía `personas` directo y saltaba la validación.
 */
export async function buscarPersonas(
  texto: string,
  opts: { limite?: number; signal?: AbortSignal } = {},
): Promise<PersonaEncontrada[]> {
  const limite = opts.limite ?? 50;
  let rpc = sb.rpc("condominio_buscar_personas", { p_texto: texto.trim() });
  if (opts.signal) rpc = rpc.abortSignal(opts.signal);
  const { data, error } = await rpc;
  if (error) throw error;
  return ((data ?? []) as any[]).slice(0, limite).map((p) => ({
    id_persona: Number(p.id_persona), nombre: String(p.nombre ?? ""), email: p.email ?? null, rfc: p.rfc ?? null, tiene_app: !!p.tiene_app,
  }));
}

/** Un nuevo titular: una persona existente, o una nueva por sus datos (si el correo existe, se reusa). */
export type NuevoTitular =
  | { tipo: "existente"; id_persona: number; nombre: string; email: string | null; tiene_app: boolean; porcentaje: string }
  | { tipo: "nueva"; nombre_legal: string; email: string; telefono: string; rfc: string; tipo_persona: "pf" | "pm"; porcentaje: string };

export const sumaPorcentajes = (t: NuevoTitular[]): number =>
  t.reduce((s, x) => s + centavos(Number(x.porcentaje) || 0), 0) / 100;

const CORREO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Qué falta para poder reasignar; `null` si está listo. Mismas reglas que la RPC. */
export function faltaParaReasignar(t: NuevoTitular[], justificacion: string): string | null {
  if (t.length === 0) return "Agrega al menos un titular.";
  for (const x of t) {
    if (x.tipo === "nueva") {
      if (!x.nombre_legal.trim()) return "Escribe el nombre del nuevo titular.";
      if (!CORREO.test(x.email.trim())) return "El nuevo titular necesita un correo válido: es su acceso a la app.";
    }
    if (centavos(Number(x.porcentaje) || 0) <= 0) return "Cada titular necesita un porcentaje mayor a cero.";
  }
  const ids = t.filter((x) => x.tipo === "existente").map((x) => (x as { id_persona: number }).id_persona);
  if (new Set(ids).size !== ids.length) return "Un titular aparece dos veces.";
  if (centavos(sumaPorcentajes(t)) !== 10000) return `Los porcentajes deben sumar 100 (suman ${sumaPorcentajes(t)}).`;
  if (justificacion.trim().length < 10) return "Explica por qué cambia el titular (mínimo 10 caracteres).";
  return null;
}

/** Sube los documentos que avalan el cambio al bucket de documentos, bajo la cuenta. */
export async function subirDocumentosCambio(idCuenta: number, archivos: File[]): Promise<DocumentoCambio[]> {
  const out: DocumentoCambio[] = [];
  for (const f of archivos) {
    const path = `titularidad/${idCuenta}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${safeExtension(f.name, "pdf")}`;
    const { error } = await supabase.storage.from("documentos").upload(path, f, { contentType: f.type || undefined, upsert: false });
    if (error) throw new Error(`No se pudo subir ${f.name}: ${error.message}`);
    const { data } = supabase.storage.from("documentos").getPublicUrl(path);
    out.push({ path, url: data.publicUrl, nombre: f.name });
  }
  return out;
}

export interface ResultadoReasignar {
  id_cambio: number;
  nuevos: TitularCambio[];
  personas_creadas: number;
}

export async function reasignarTitularidad(args: {
  idCuenta: number;
  titulares: NuevoTitular[];
  justificacion: string;
  documentos: DocumentoCambio[];
  idSolicitud?: number | null;
}): Promise<ResultadoReasignar> {
  const p_titulares = args.titulares.map((t) =>
    t.tipo === "existente"
      ? { id_persona: t.id_persona, porcentaje: Number(t.porcentaje) }
      : {
        nombre_legal: t.nombre_legal.trim(), email: t.email.trim().toLowerCase(), telefono: t.telefono.trim() || null,
        rfc: t.rfc.trim().toUpperCase() || null, tipo_persona: t.tipo_persona, porcentaje: Number(t.porcentaje),
      });
  const { data, error } = await sb.rpc("condominio_reasignar_titularidad", {
    p_id_cuenta: args.idCuenta,
    p_titulares,
    p_justificacion: args.justificacion.trim(),
    p_documentos: args.documentos,
    p_id_solicitud: args.idSolicitud ?? null,
  });
  if (error) throw error;
  return data as ResultadoReasignar;
}

export async function resolverSolicitud(id: number, estatus: "rechazada" | "en_revision", comentario: string): Promise<void> {
  const { error } = await sb.rpc("condominio_resolver_solicitud_titularidad", {
    p_id_solicitud: id, p_estatus: estatus, p_comentario: comentario.trim(),
  });
  if (error) throw error;
}
